/**
 * Auth Utilities
 *
 * Handles authentication token storage and store URL management.
 * Currently uses js-cookie for Bearer token auth.
 * Will migrate to httpOnly cookies once the backend supports it.
 */
import Cookies from 'js-cookie';

// ─── Constants ────────────────────────────────────────────────────

const AUTH_COOKIE_NAME = 'auth_token';
const STORE_URL_KEY = 'compra_store_url';

// ─── Auth Token (via js-cookie — current mechanism) ───────────────

/**
 * Get the auth token from cookies.
 */
export const getAuthToken = (): string | undefined => {
    return Cookies.get(AUTH_COOKIE_NAME);
};

/**
 * Set the auth token in cookies.
 */
export const setAuthToken = (token: string): void => {
    Cookies.set(AUTH_COOKIE_NAME, token, {
        path: '/',
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax',
        expires: 7, // 7 days
    });
};

/**
 * Clear the auth token from cookies.
 */
export const clearAuthToken = (): void => {
    Cookies.remove(AUTH_COOKIE_NAME, { path: '/' });
};

// ─── Store URL ────────────────────────────────────────────────────

/**
 * Get the current store URL from localStorage.
 */
export const getStoreUrl = (): string | null => {
    try {
        return localStorage.getItem(STORE_URL_KEY);
    } catch {
        return null;
    }
};

/**
 * Set the store URL in localStorage.
 */
export const setStoreUrl = (url: string): void => {
    try {
        localStorage.setItem(STORE_URL_KEY, url);
    } catch {
        console.warn('Unable to save store URL to localStorage');
    }
};

/**
 * Remove the store URL from localStorage.
 */
export const removeStoreUrl = (): void => {
    try {
        localStorage.removeItem(STORE_URL_KEY);
    } catch {
        console.warn('Unable to remove store URL from localStorage');
    }
};

// ─── JWT Decode (legacy compat) ───────────────────────────────────

/**
 * Decode the auth token payload without verification.
 * Used by legacy code — prefer auth slice selectors for new code.
 */
export const getDecodedAuthToken = (): any | null => {
    const token = getAuthToken();
    if (!token) return null;

    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        return JSON.parse(atob(payload));
    } catch {
        return null;
    }
};

/**
 * Extract merchant data from the decoded token.
 * Used by legacy code — prefer selectMerchantId from auth slice.
 */
export const getMerchantData = (): any | null => {
    const decoded = getDecodedAuthToken();
    return decoded || null;
};
